import React, { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import type { Locale } from '@/i18n/config';
import { CandidateAssessmentSummary } from '../../types/job.types';
import { getFixedTForContentLocale } from '@/utils/locale';
import { Tag } from '../ui/atoms/Tag';
import { Tooltip } from '../ui/tooltip';
import { Typography } from '../ui/atoms/Typography';

interface PsychometricResultsProps {
  psychometricResults: CandidateAssessmentSummary['psychometric_results'];
  contentLocale?: Locale;
}

type DiscTraitKey = 'D' | 'I' | 'S' | 'C';

type MbtiDimension = {
  key: string;
  leftLabel: string;
  leftLetter: string;
  rightLabel: string;
  rightLetter: string;
};

const DISC_TRAIT_ORDER: DiscTraitKey[] = ['D', 'I', 'S', 'C'];

function buildTooltipContent(letter: string, label: string, explanation?: string): React.ReactNode {
  if (!explanation) return null;
  const detail = explanation.replace(/^[^:]+:\s*/, '');
  return (
    <span>
      <strong>{letter} — {label}:</strong> {detail}
    </span>
  );
}

function computeDiscPlotFromPercentages(percentages?: Record<string, number>) {
  const angleByTrait: Record<string, number> = {
    D: (7 * Math.PI) / 4,
    I: Math.PI / 4,
    S: (3 * Math.PI) / 4,
    C: (5 * Math.PI) / 4,
  };
  const safe = (trait: string) => Math.max(0, Math.min(1, (percentages?.[trait] ?? 0) / 100));
  const x = ['D', 'I', 'S', 'C'].reduce((acc, trait) => acc + safe(trait) * Math.cos(angleByTrait[trait]), 0);
  const y = ['D', 'I', 'S', 'C'].reduce((acc, trait) => acc + safe(trait) * Math.sin(angleByTrait[trait]), 0);
  const angle = ((Math.atan2(y, x) % (2 * Math.PI)) + (2 * Math.PI)) % (2 * Math.PI);
  const magnitude = Math.sqrt(x * x + y * y);
  return { resultant_angle: angle, resultant_magnitude: magnitude };
}

function toSvgAngle(sourceAngle: number): number {
  const full = 2 * Math.PI;
  return ((Math.PI / 2 - sourceAngle) % full + full) % full;
}

const DiscPolarChart: React.FC<{
  disc: NonNullable<CandidateAssessmentSummary['psychometric_results']>['disc'];
  chartTitle: string;
  traitLabels: Record<DiscTraitKey, string>;
}> = ({ disc, chartTitle, traitLabels }) => {
  if (!disc) return null;
  const size = 280;
  const center = size / 2;
  const radius = 110;
  const computedPlot = computeDiscPlotFromPercentages(disc.percentages);
  const sourceAngle = computedPlot.resultant_angle;
  const angle = toSvgAngle(sourceAngle);
  const magnitude = computedPlot.resultant_magnitude;
  const clampedMagnitude = Math.max(0, Math.min(1, magnitude));
  const pointX = center + radius * clampedMagnitude * Math.cos(angle);
  const pointY = center - radius * clampedMagnitude * Math.sin(angle);
  const labelRadius = radius + 12;
  const axisLabelPos = {
    D: { x: center + labelRadius * Math.cos((3 * Math.PI) / 4), y: center - labelRadius * Math.sin((3 * Math.PI) / 4) },
    I: { x: center + labelRadius * Math.cos(Math.PI / 4), y: center - labelRadius * Math.sin(Math.PI / 4) },
    S: { x: center + labelRadius * Math.cos((7 * Math.PI) / 4), y: center - labelRadius * Math.sin((7 * Math.PI) / 4) },
    C: { x: center + labelRadius * Math.cos((5 * Math.PI) / 4), y: center - labelRadius * Math.sin((5 * Math.PI) / 4) },
  };

  return (
    <div className="max-w-[360px] mx-auto">
      <Typography as="p" variant="body-md" weight="bold" color="primary" className="mb-2">{chartTitle}</Typography>
      <svg width="90%" className="mx-auto" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={chartTitle}>
        <circle cx={center} cy={center} r={radius} fill="#fbf7ff" stroke="#d8b4fe" strokeWidth="1.25" />
        <circle cx={center} cy={center} r={radius * 0.8} fill="none" stroke="#e9d5ff" strokeWidth="1" />
        <circle cx={center} cy={center} r={radius * 0.6} fill="none" stroke="#e9d5ff" strokeWidth="1" />
        <circle cx={center} cy={center} r={radius * 0.4} fill="none" stroke="#eee3ff" strokeWidth="1" />
        <circle cx={center} cy={center} r={radius * 0.2} fill="none" stroke="#f3e8ff" strokeWidth="1" />
        <line x1={center} y1={center - radius} x2={center} y2={center + radius} stroke="#b9b2c5" strokeDasharray="4 4" />
        <line x1={center - radius} y1={center} x2={center + radius} y2={center} stroke="#b9b2c5" strokeDasharray="4 4" />
        <line x1={center - radius * 0.707} y1={center - radius * 0.707} x2={center + radius * 0.707} y2={center + radius * 0.707} stroke="#d5d0de" strokeWidth="0.8" />
        <line x1={center - radius * 0.707} y1={center + radius * 0.707} x2={center + radius * 0.707} y2={center - radius * 0.707} stroke="#d5d0de" strokeWidth="0.8" />
        <circle cx={pointX} cy={pointY} r="4" fill="#7c3aed" />
        {(Object.keys(axisLabelPos) as Array<keyof typeof axisLabelPos>).map((trait) => (
          <text
            key={trait}
            x={axisLabelPos[trait].x}
            y={axisLabelPos[trait].y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fontWeight="700"
            fill="#111827"
          >
            {trait}
          </text>
        ))}
      </svg>
      <div className="grid grid-cols-2 gap-x-4 text-[9px] sm:text-[10px]">
        {DISC_TRAIT_ORDER.map((trait) => {
          const percentage = Math.round(disc.percentages?.[trait] ?? 0);
          const isPrimary = disc.primary === trait;
          const isSecondary = disc.secondary === trait;

          return (
            <div key={trait} className="flex items-center justify-between gap-2 rounded bg-white/70 px-2 py-1.5 whitespace-nowrap">
              <Typography as="span" variant="body-xs" color="secondary" weight="medium">{trait}: {traitLabels[trait]}</Typography>
              <Typography as="span" variant="body-xs" color={isPrimary ? 'primary' : isSecondary ? 'secondary' : 'muted'} weight="medium">{percentage}%</Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DimensionBar: React.FC<{
  dim: MbtiDimension;
  percentages: Record<string, number>;
  typeExplanation?: Record<string, string>;
}> = ({ dim, percentages, typeExplanation }) => {
  const leftPct = percentages[dim.leftLetter] ?? 50;
  const rightPct = percentages[dim.rightLetter] ?? 50;
  const dominantIsLeft = leftPct >= rightPct;

  const leftTip = buildTooltipContent(dim.leftLetter, dim.leftLabel, typeExplanation?.[dim.leftLetter]);
  const rightTip = buildTooltipContent(dim.rightLetter, dim.rightLabel, typeExplanation?.[dim.rightLetter]);

  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between mb-1">
        <Tooltip content={leftTip} variant="top" className="max-w-[220px] !whitespace-normal">
          <span className={`text-[11px] cursor-default font-heading leading-none ${dominantIsLeft ? 'text-dark-invirtus font-semibold' : 'text-dark-invirtus/40'}`}>
            {dim.leftLabel}
          </span>
        </Tooltip>
        <Tooltip content={rightTip} variant="top" className="max-w-[220px] !whitespace-normal">
          <span className={`text-[11px] cursor-default font-heading leading-none ${!dominantIsLeft ? 'text-dark-invirtus font-semibold' : 'text-dark-invirtus/40'}`}>
            {dim.rightLabel}
          </span>
        </Tooltip>
      </div>

      <div className="flex items-center gap-1">
        <Tooltip content={leftTip} variant="bottom" className="max-w-[220px] !whitespace-normal">
          <span className={`flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-[10px] font-bold font-heading cursor-default ${
            dominantIsLeft
              ? 'bg-purple/20 text-dark-invirtus border-2 border-purple'
              : 'bg-gray-100 text-dark-invirtus/35 border border-gray-200'
          }`}>
            {Math.round(leftPct)}%
          </span>
        </Tooltip>

        <div className="flex-1 relative h-2 rounded-full bg-gray-200 overflow-hidden">
          {dominantIsLeft ? (
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-purple to-purple/40 transition-all duration-500"
              style={{ width: `${leftPct}%` }}
            />
          ) : (
            <div
              className="absolute right-0 top-0 h-full rounded-full bg-gradient-to-l from-purple to-purple/40 transition-all duration-500"
              style={{ width: `${rightPct}%` }}
            />
          )}
          <div className="absolute left-1/2 top-0 w-px h-full bg-white/60" />
        </div>

        <Tooltip content={rightTip} variant="bottom" className="max-w-[220px] !whitespace-normal">
          <span className={`flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-[10px] font-bold font-heading cursor-default ${
            !dominantIsLeft
              ? 'bg-purple/20 text-dark-invirtus border-2 border-purple'
              : 'bg-gray-100 text-dark-invirtus/35 border border-gray-200'
          }`}>
            {Math.round(rightPct)}%
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

interface AssessmentContentBlockProps {
  summary?: string;
  strengths?: string[];
  watchOuts?: string[];
  summaryLabel: string;
  strengthsLabel: string;
  watchOutsLabel: string;
  rightPanel: React.ReactNode;
}

function AssessmentContentBlock({
  summary,
  strengths = [],
  watchOuts = [],
  summaryLabel,
  strengthsLabel,
  watchOutsLabel,
  rightPanel,
}: AssessmentContentBlockProps) {
  return (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    <div className="space-y-2.5">
      {summary && (
        <div>
          <Typography as="p" variant="body-md" weight="bold" color="primary">{summaryLabel}</Typography>
          <Typography as="p" variant="body-sm" color="secondary" weight="medium" className="leading-relaxed mt-0.5">
            {summary}
          </Typography>
        </div>
      )}

      {strengths.length > 0 && (
        <div>
          <Typography as="p" variant="body-md" weight="bold" color="primary">{strengthsLabel}</Typography>
          <ul className="list-disc ml-4 text-xs text-dark-invirtus/60 font-body mt-0.5 space-y-0.5">
            {strengths.map((s, i) => (
              <Typography as="li" variant="body-sm" color="secondary" weight="medium" key={`strength-${i}`}>{s}</Typography>
            ))}
          </ul>
        </div>
      )}

      {watchOuts.length > 0 && (
        <div>
          <Typography as="p" variant="body-md" weight="bold" color="primary">{watchOutsLabel}</Typography>
          <ul className="list-disc ml-4 text-xs text-dark-invirtus/60 font-body mt-0.5 space-y-0.5">
            {watchOuts.map((w, i) => (
              <Typography as="li" variant="body-sm" color="secondary" weight="medium" key={`watch-out-${i}`}>{w}</Typography>
            ))}
          </ul>
        </div>
      )}
    </div>

    <div>
      {rightPanel}
    </div>
  </div>
  );
}

export const PsychometricResults: React.FC<PsychometricResultsProps> = ({
  psychometricResults,
  contentLocale = 'en',
}) => {
  const tContent = useMemo<TFunction<'candidates'>>(
    () => getFixedTForContentLocale(contentLocale),
    [contentLocale],
  );
  const mbti = psychometricResults?.mbti;
  const disc = psychometricResults?.disc;
  const [isMbtiExpanded, setIsMbtiExpanded] = useState(false);
  const [isDiscExpanded, setIsDiscExpanded] = useState(false);

  const mbtiDimensions = useMemo((): MbtiDimension[] => [
    {
      key: 'EI',
      leftLabel: tContent('meta_cv.psychometric.mbti.extraversion'),
      leftLetter: 'E',
      rightLabel: tContent('meta_cv.psychometric.mbti.introversion'),
      rightLetter: 'I',
    },
    {
      key: 'SN',
      leftLabel: tContent('meta_cv.psychometric.mbti.sensing'),
      leftLetter: 'S',
      rightLabel: tContent('meta_cv.psychometric.mbti.intuition'),
      rightLetter: 'N',
    },
    {
      key: 'TF',
      leftLabel: tContent('meta_cv.psychometric.mbti.thinking'),
      leftLetter: 'T',
      rightLabel: tContent('meta_cv.psychometric.mbti.feeling'),
      rightLetter: 'F',
    },
    {
      key: 'JP',
      leftLabel: tContent('meta_cv.psychometric.mbti.judging'),
      leftLetter: 'J',
      rightLabel: tContent('meta_cv.psychometric.mbti.perceiving'),
      rightLetter: 'P',
    },
  ], [tContent]);

  const discTraitLabels = useMemo((): Record<DiscTraitKey, string> => ({
    D: tContent('meta_cv.psychometric.disc_traits.D'),
    I: tContent('meta_cv.psychometric.disc_traits.I'),
    S: tContent('meta_cv.psychometric.disc_traits.S'),
    C: tContent('meta_cv.psychometric.disc_traits.C'),
  }), [tContent]);

  if (!mbti && !disc) return null;

  const mbtiView = mbti ? {
    type: mbti.type,
    percentages: mbti.percentages ?? {},
    title: mbti.title,
    summary: mbti.executive_summary,
    typeExplanation: mbti.type_explanation,
    strengths: mbti.strengths ?? [],
    watchOuts: mbti.watch_outs ?? [],
  } : null;

  return (
    <div className="space-y-4">
      {mbti && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsMbtiExpanded(!isMbtiExpanded)}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h6>{tContent('meta_cv.psychometric.mbti_header')}</h6>
              <Tag intent="info" variant="subtle">
                <span className="text-sm font-semibold tracking-wider">
                  {mbtiView?.type}{mbtiView?.title ? ` — ${mbtiView.title}` : ''}
                </span>
              </Tag>
            </div>
            <svg
              className={`w-5 h-5 text-dark-invirtus transform transition-transform flex-shrink-0 ${isMbtiExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isMbtiExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <AssessmentContentBlock
                summary={mbtiView?.summary}
                strengths={mbtiView?.strengths}
                watchOuts={mbtiView?.watchOuts}
                summaryLabel={tContent('meta_cv.psychometric.summary')}
                strengthsLabel={tContent('meta_cv.psychometric.strengths')}
                watchOutsLabel={tContent('meta_cv.psychometric.watch_outs')}
                rightPanel={(
                  <>
                    <strong className="font-heading text-dark-invirtus/90 text-sm">{tContent('meta_cv.psychometric.type_explanation')}</strong>

                    <div className="space-y-0">
                      {mbtiDimensions.map((dim) => (
                        <DimensionBar
                          key={dim.key}
                          dim={dim}
                          percentages={mbtiView?.percentages ?? {}}
                          typeExplanation={mbtiView?.typeExplanation}
                        />
                      ))}
                    </div>
                  </>
                )}
              />
            </div>
          )}
        </div>
      )}

      {disc && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsDiscExpanded(!isDiscExpanded)}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h6>{tContent('meta_cv.psychometric.disc_header')}</h6>
              <Tag intent="info" variant="subtle">
                <span className="text-sm font-semibold tracking-wider">
                  {disc.primary}/{disc.secondary}
                </span>
              </Tag>
            </div>
            <svg
              className={`w-5 h-5 text-dark-invirtus transform transition-transform flex-shrink-0 ${isDiscExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isDiscExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <AssessmentContentBlock
                summary={disc.description}
                strengths={disc.strengths ?? []}
                watchOuts={disc.challenges ?? []}
                summaryLabel={tContent('meta_cv.psychometric.summary')}
                strengthsLabel={tContent('meta_cv.psychometric.strengths')}
                watchOutsLabel={tContent('meta_cv.psychometric.watch_outs')}
                rightPanel={(
                  <div className="w-full lg:justify-self-end">
                    <DiscPolarChart
                      disc={disc}
                      chartTitle={tContent('meta_cv.psychometric.disc_profile')}
                      traitLabels={discTraitLabels}
                    />
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PsychometricResults;
